// @ts-check
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  NLP_STAGE,
  setNLPModalState,
  setShowNLPFilterModal,
} from 'actions/searchNLP';
import { Button, Icon } from 'components/common';
import { openMainSearchWithQuery } from 'components/SearchNLP/orgRedirectContract';
import { useAuth } from 'hooks';
import { getFilters } from 'selectors/organisation';
import { getAiSession } from 'selectors/searchNLP';
import { aiSearchProductName } from 'utils/constants';
import { colours } from 'utils/theme';

// SCIL-18510: what the People/Activity tables show when a SciAI search on the
// profile matches nothing. The search already spent one of the user's daily
// quota, so a bare "No data" is the worst outcome: name the org, the record type
// and the query, then offer the two ways forward.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  text-align: center;
  color: ${colours.character.primary85};
`;

const Heading = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
`;

const Query = styled.p`
  margin: 0;
  font-style: italic;
  color: ${colours.grey};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

/**
 * The accepted SciAI query behind the org's current People/Activity filters, or
 * '' for a plain filtered search. Follows the session id the org slice stamps
 * on apply rather than the global confirmed pointer, which another org's search
 * can move. Licence-gated like every other SciAI surface, since session state
 * can outlive a licence.
 *
 * @param {number} organisationId
 * @returns {string}
 */
export const useOrgSciAiQuery = organisationId => {
  const { user } = useAuth();
  const filters = useSelector(getFilters(organisationId));
  const session = useSelector(getAiSession(filters?.aiFilterSessionId));
  if (!user?.hasSciSearchAI) return '';
  // The chain ends with whatever the user most recently ran.
  const queries = session?.naturalLanguageQuery?.queries ?? [];
  return queries[queries.length - 1] ?? '';
};

/**
 * True once a People response is in and matched nobody. The distinct count is an
 * OpenSearch cardinality, which is null rather than 0 over zero hits.
 *
 * @param {{ distinctSuperResearchersCount?: number | null } | undefined} researchers
 * @returns {boolean}
 */
export const hasNoPeopleResults = researchers =>
  Boolean(researchers) &&
  (researchers.distinctSuperResearchersCount ?? 0) === 0;

/**
 * @param {Object} props
 * @param {string} props.organisationName
 * @param {'People' | 'Activity'} props.recordLabel
 * @param {string} props.query the SciAI query that matched nothing
 */
export const OrgSciAiEmptyState = ({
  organisationName,
  recordLabel,
  query,
}) => {
  const dispatch = useDispatch();

  const editQuery = () => {
    dispatch(setNLPModalState({ query, stage: NLP_STAGE.INPUT }));
    dispatch(setShowNLPFilterModal(true));
  };

  return (
    <Container data-testid='org-sciai-empty-state'>
      <Icon icon='sparkles' color={colours.linkBlue} size='lg' />
      <Heading>
        {organisationName} has no {recordLabel} matching this{' '}
        {aiSearchProductName} query.
      </Heading>
      <Query>“{query}”</Query>
      <span>
        Try rephrasing it, or run it on main search to look across all
        organisations.
      </span>
      <Actions>
        <Button
          type='secondary'
          data-testid='org-sciai-edit-query'
          onClick={editQuery}
        >
          Edit query
        </Button>
        <Button
          type='primary'
          data-testid='org-sciai-run-on-main-search'
          onClick={() => openMainSearchWithQuery(query)}
        >
          Run on main search
        </Button>
      </Actions>
    </Container>
  );
};
